import { Auth, AWSHub as Hub } from 'aws-amplify';
import { setAmplifyForLocalHost } from './config.app';
import { getDynamicConfig } from './useDynamicConfig';
import { initialAuthenticatedRoute } from '@vl-core/initialAuthenticatedRoute';
import { User } from '../schemas/shared';
import { getSessionUser } from './getSessionUser';

const { REACT_APP_UI_VERSION, REACT_APP_SETTINGS_URL, REACT_APP_BASEPATH, NODE_ENV, REACT_APP_HAR_ENABLED } =
  import.meta.env || process.env;

if (typeof window !== 'undefined') {
  window.global ||= window;

  // @ts-ignore
  window.process ||= {
    env: {
      REACT_APP_UI_VERSION,
      REACT_APP_SETTINGS_URL,
      REACT_APP_BASEPATH,
      NODE_ENV,
      PUBLIC_URL: REACT_APP_BASEPATH,
      REACT_APP_HAR_ENABLED
    }
  };
}

function tryTransferSession() {
  if (window.location.hash.startsWith('#/transfer-session=')) {
    try {
      const encodedData = window.location.hash.split('#/transfer-session=')[1];
      const serializedStorage = JSON.parse(atob(encodedData)); // Decode Base64

      // Inject the transferred keys back into this URL's sessionStorage
      Object.keys(serializedStorage).forEach((key) => {
        sessionStorage.setItem(key, serializedStorage[key]);
      });

      // Crucial: Strip the ugly hash out of the address bar cleanly
      window.history.replaceState(null, '', window.location.pathname + window.location.search);

      return true;
    } catch (e) {
      console.error('Failed to unpack transferred session:', e);
    }
  }

  return false;
}

function deriveBaseUrl(user: User, storyBranch: string) {
  const { build_path, login_url } = user;
  const u = new URL(login_url);

  u.pathname = build_path || storyBranch;

  return u.toString();
}

function transferToLoginDomain(login_url: string, storyBranch: string) {
  const authSessionData = Object.fromEntries(
    Object.entries({ ...sessionStorage }).filter(
      ([key]) => key.startsWith('amplify') || key.includes('CognitoIdentityServiceProvider')
    )
  );
  const serializedPayload = btoa(JSON.stringify(authSessionData));

  const storySegment = storyBranch ? `/${storyBranch}` : '';
  window.location.replace(`${new URL(login_url).origin}${storySegment}/#/transfer-session=${serializedPayload}`);

  return true;
}

export async function deriveAppRoute(user: User) {
  return initialAuthenticatedRoute(new URL(window.location.href), user);
}

// Called after the user is signed in, BUT BEFORE REACT HAS STARTED. This happens on the signIn redirect
// of an OAuth SSO or on a browser refresh. It does NOT get called after an MFA code is processed.
// - the correct app URL if they are signed in to the wrong app
// - the correct A/B build_path if A/B testing is applicable for that user
// - the correct story branch if they are not on it already
const maybeRedirectAuthenticatedUser = async (storyBranch = ''): Promise<boolean> => {
  const { tokens } = await Auth.fetchAuthSession();
  if (!tokens) throw Error('maybeRedirectAuthenticatedUser() should only be called when signed in.');
  if (storyBranch.match(/[/?#]/))
    throw Error(`maybeRedirectAuthenticatedUser() storyBranch should be a simple name: "${storyBranch}"`);

  const user = await getSessionUser().catch((e) => {
    const errMsg = 'Failed to fetch user data';
    console.error(errMsg, e);
    throw Error(errMsg);
  });

  const { login_url } = user;

  if (['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)) return false;
  if (!window.location.href.startsWith(login_url)) return transferToLoginDomain(login_url, storyBranch);

  const targetBaseUrl = deriveBaseUrl(user, storyBranch);
  if (!window.location.href.startsWith(targetBaseUrl)) {
    const derivedRoute = await deriveAppRoute(user);
    window.location.replace(targetBaseUrl + derivedRoute);
    return true;
  }

  // do not worry about SPA navigation here ... if we get to here, we've got the right code executing.
  // leave it up to React to navigate as soon as its router is operational
  return false;
}

// Returns true if the user is redirected away if:
// - the user has signed in to the wrong app/build_path
// - the user is doing A/B testing and is on the wrong story branch
// - they are on the wrong story branch after an SSO
export const authInit = async (): Promise<boolean> => {
  let capturedStoryBranch = '';

  // 1. Setup the Hub to watch the application lifecycle
  const unsubscribe = Hub.listen('auth', async ({ payload }) => {
    if (payload.event === 'customOAuthState') {
      capturedStoryBranch = JSON.parse(atob(payload.data)).toString().split('/')[1];
      unsubscribe();
    }
  });

  try {
    const { config } = await getDynamicConfig();

    window.config = config;
    tryTransferSession();
    setAmplifyForLocalHost(config);

    // Apparently Amplify has a bug (#13534) where fetchAuthSession() doesn't detect an in-flight sign-in redirect
    // without a microtask yield. https://github.com/aws-amplify/amplify-js/issues/13534
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Blocks until Amplify initializes storage from any redirect parameters
    const { tokens } = await Auth.fetchAuthSession();
    if (tokens) return maybeRedirectAuthenticatedUser(capturedStoryBranch);
  } catch (error) {
    console.error('Critical application bootstrap failure:', error);
  } finally {
    unsubscribe();
  }

  return false; // Safe to mount React
}
